<template>
    <div class="mb-4">
        <div class="flex items-center gap-1">
            <h3 class="mb-1 text-sm font-semibold text-gray-900">Property Description</h3>
            <ToolTip
                v-if="isSharedViewOnly && viewOnlyTooltip && !property.deleted_at"
                :title="viewOnlyTooltip.title"
                :description="viewOnlyTooltip.description"
            />
        </div>
        <div v-if="!editingDescription">
            <template v-if="property.description">
                <p class="whitespace-pre-wrap text-sm leading-normal text-gray-600">
                    <span v-if="!showFullDescription">{{ truncatedDescription }}</span>
                    <span v-else>{{ property.description }}</span>
                    <!-- 86bc2cgxd: the edit pencil sits right after the text. -->
                    <button
                        v-if="!property.deleted_at"
                        type="button"
                        class="ml-1 inline-flex p-0.5 align-baseline text-gray-500 hover:text-gray-800 disabled:pointer-events-none disabled:opacity-50"
                        aria-label="Edit description"
                        :disabled="isSharedViewOnly"
                        @click="startDescriptionEdit"
                    >
                        <font-awesome-icon :icon="faPenToSquare" class="h-3.5 w-3.5" />
                    </button>
                </p>
                <button
                    v-if="property.description && property.description.length > 100"
                    type="button"
                    class="mt-1 text-xs text-blue-600 hover:text-blue-800"
                    @click="showFullDescription = !showFullDescription"
                >
                    {{ showFullDescription ? 'See Less' : 'See More' }}
                </button>
            </template>
            <button
                v-else-if="!property.deleted_at"
                type="button"
                class="text-sm text-blue-600 hover:text-blue-800 disabled:pointer-events-none disabled:opacity-50"
                :disabled="isSharedViewOnly"
                @click="startDescriptionEdit"
            >
                Add description
            </button>
        </div>
        <div v-else class="space-y-2">
            <textarea
                v-model="descriptionForm.description"
                rows="6"
                class="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <p v-if="descriptionForm.errors.description" class="text-sm text-red-600">
                {{ descriptionForm.errors.description }}
            </p>
            <div class="flex gap-2">
                <button
                    type="button"
                    class="rounded-md bg-rent-risk-blue-400 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                    :disabled="descriptionForm.processing"
                    @click="saveDescription"
                >
                    Save
                </button>
                <button
                    type="button"
                    class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700"
                    @click="cancelDescriptionEdit"
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons'
import ToolTip from '@/Shared/ToolTip.vue'

const route =
    (typeof window !== 'undefined' && window.route) ||
    ((..._args) => '#')

const props = defineProps({
    property: {
        type: Object,
        required: true,
    },
    propertyAccess: {
        type: String,
        default: 'owner',
    },
    viewOnlyTooltip: {
        type: Object,
        default: null,
    },
})

const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')

const editingDescription = ref(false)
const showFullDescription = ref(false)

const descriptionForm = useForm({ description: '' })

const truncatedDescription = computed(() => {
    if (!props.property.description) {
        return ''
    }

    return props.property.description.length > 100
        ? props.property.description.substring(0, 100) + '...'
        : props.property.description
})

const startDescriptionEdit = () => {
    if (isSharedViewOnly.value) {
        return
    }
    editingDescription.value = true
    descriptionForm.clearErrors()
    descriptionForm.description = props.property.description || ''
}

const cancelDescriptionEdit = () => {
    editingDescription.value = false
    descriptionForm.clearErrors()
    descriptionForm.description = props.property.description || ''
}

const saveDescription = () => {
    descriptionForm.clearErrors()
    descriptionForm.patch(route('properties.update-description', props.property.id), {
        preserveScroll: true,
        only: ['property'],
        onSuccess: () => {
            editingDescription.value = false
        },
    })
}
</script>
